<template lang="pug">
v-badge.x-widget-link(
  :value="!!badge"
  :content="badgeText"
  :color="badgeColor"
  :class="badgeClass"
  bordered
  overlap
  left
  offset-x="160"
)
  v-tooltip(bottom)
    template(#activator="{ on, attrs }")
      v-btn(
        v-bind="attrs"
        v-on="on"
        :icon="!isText"
        :color="linkColor"
        :class="linkClass"
        :disabled="isDisabled"
        @click="click"
      )
        v-hover(v-slot="{ hover }")
          v-icon(
            v-if="!!getIcon(hover)"
            :color="getColor(hover, isText)"
            :left="isText"
          )
            | {{ getIcon(hover) }}

        div(v-if="isText") {{ verb }}

    span
      span.text-md.font-weight-medium {{ title }}
      br
      span.font-weight-light {{ description }}
</template>

<style>
.x-widget-link .v-badge__badge {
  z-index: 100;
}
</style>

<script lang="js">
import { hasRel } from '@/helpers/rels'
import { selectClass, selectColor } from '@/helpers/styles'
import { interpolate, isInterpolated } from '@/helpers/strings'
import { solveExpressionProperty } from '@/helpers/expressions'

export default {
  props: {
    /**
     * Objeto contendo os valores e campos do contexto no qual o widget está
     * inserido para inferência de propriedades aplicadas em interpolação de
     * templates de texto.
     */
    context: Object,

    /**
     * Instância opcional de um campo do Paper.
     * @type {Keep.Paper.Design.Field}
     */
    field: Object,

    /**
     * Instância opcional de um link.
     * Quando informado as propriedades são lidas deste link em vez de `field`.
     * @type {Keep.Paper.Design.Link}
     */
    action: Object,
  },

  computed: {
    // myName: vm => vm.getIt('name'),
    // myTitle: vm => vm.getIt('title') || vm.myName,
    // myDescription: vm => vm.getIt('description'),
    // myHint: vm => vm.getIt('hint'),
    // myPlaceholder: vm => vm.getIt('placeholder'),
    // isSecure: vm => vm.hasIt('secure'),
    // isLarge: vm => vm.hasIt('large'),
    // hasNoHints: vm => vm.hasIt('noHints'),
    isDisabled: vm => vm.hasIt('disabled'),
    // isRequired: vm => vm.hasIt('required'),
    // isReadOnly: vm => vm.hasIt('readOnly') || vm.hasIt('readonly'),
    // myIcon: vm => vm.getIt('icon'),

    content: {
      get () {
        return this.value || null
      },
      set (value) {
        this.$emit('input', value || null)
      }
    },

    link () {
      let link = this.action
      if (!link) {
        const links = this.field?.links || []
        const actions = links.filter(link => hasRel(link, [ 'Action' ]))
        link = actions[0] || {}
      }
      link.eref = this.format(link.eref, this.context)

      if (link?.expression) {
        solveExpressionProperty({ ...this.field, ...link }, link, (this.context || {}))
      }

      return link
    },

    verb () {
      return this.format(
        this.link.verb || this.link.title || this.link.name || this.link.eref)
    },

    title () {
      return this.format(
        this.link.title || this.link.verb || this.link.name || this.link.eref)
    },

    description () {
      return this.format(this.link.description)
    },

    badge () {
      const badge = this.link.badge
      return badge?.text ? badge : null
    },

    badgeText() {
      return this.format(this.badge?.text)
    },

    badgeClass() {
      const badge = this.badge
      const clazz = selectClass(badge?.style)
      return this.format(clazz)
    },

    badgeColor() {
      const badge = this.badge
      if (badge?.color) return this.format(badge?.color)
      if (badge?.style) return this.format(selectColor(badge?.style))
      return null
    },

    linkClass() {
      const action = this.pin || this.link
      const clazz = selectClass(action?.style)
      return this.format(clazz)
    },

    linkColor() {
      const color = this.pin?.color || this.link?.color
      if (color) return this.format(color)

      const style = this.pin?.style || this.link?.style
      if (style) return this.format(selectColor(style))

      if (hasRel(this.pin, 'Accent') || hasRel(this.link, 'Accent')) return 'accent'
      if (hasRel(this.pin, 'Primary') || hasRel(this.link, 'Primary')) return 'primary'
      return null
    },

    // PIN será usado para prender o ícone nos cantos do form...
    // Mas por enquanto está sem efeito.
    pin () {
      // return this.link.states?.filter(state => state.name === 'Pin')[0] || {}
      return {}
    },

    isText () {
      return !hasRel(this.pin, 'Icon')
          && !hasRel(this.link, 'Icon')
    },

    isIcon () {
      return !hasRel(this.pin, 'Text')
          && !hasRel(this.link, 'Text')
          && (this.pin.icon || this.link.icon)
    },
  },

  methods: {
    getIt (property) {
      return this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
        || this.link?.[property]
    },
    hasIt (property) {
      if (this.$props[property] === '') return true
      if (this.$attrs[property] === '') return true
      const value = this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
        || this.link?.[property]
      return !!value
    },

    getIcon (active) {
      const icon = active
        ? this.link.icon || this.pin.icon
        : this.pin.icon || this.link.icon
      return this.format(icon)
    },

    getStyle (active) {
      if (active) return "primary--text"
      const clazz = selectClass(this.pin.style || this.link.style)
      return this.format(clazz)
    },

    getColor (active, isText) {
      if (active) return 'primary'
      if (isText) return
      const { pin, link } = this
      const color = pin.color || link.color || selectColor(pin.style || link.style)
      return this.format(color)
    },

    click(args) {

      const commitValue = this.link?.value
      if (commitValue !== undefined && commitValue !== null) {
        this.content = commitValue
      }

      const action = this.link
      this.$emit('click', { action, args })
    },

    format(text) {
      return isInterpolated(text) ? interpolate(text, this.context) : text
    },
  },
}
</script>
